import React from 'react'
import type { Arrangement } from '../types/arrangement'

interface ArrangementListProps {
  items: Arrangement[]
  mode: 'e2' | 'e3' | 'e1'
  onChange?: (id: string, fields: Partial<Omit<Arrangement, 'id'>>) => void
  onToggleDone?: (id: string) => void
  onRemove?: (id: string) => void
  onFocusChange?: (id: string | null, cursorPos: number) => void
}

const SPRING = 'cubic-bezier(0.25, 1, 0.2, 1)'

// CSS dla ukrycia scrollbarów w textarea i stylów E3 (SearchBar z E1)
const arrangementStylesCSS = `
  .arrangement-text-container textarea::-webkit-scrollbar {
    display: none;
  }
  .arrangement-tile-e3 {
    background: linear-gradient(180deg, rgba(25, 25, 25, 0.24) 0%, rgba(15, 15, 15, 0.4) 100%);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 17.5px;
    backdrop-filter: blur(16.5px);
    -webkit-backdrop-filter: blur(16.5px);
    box-shadow: inset 0px 1px 1.5px rgba(255, 255, 255, 0.15), 0px 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export const ArrangementList: React.FC<ArrangementListProps> = ({
  items,
  mode,
  onChange,
  onToggleDone,
  onRemove,
  onFocusChange,
}) => {
  if (items.length === 0 && mode !== 'e3') {
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={mode === 'e3' ? 'arrangement-tile-e3' : undefined}
          style={{
            background: mode === 'e3'
              ? undefined  // E3: tło z CSS .arrangement-tile-e3
              : 'rgba(0,255,85,0.05)',
            border: mode === 'e3' ? undefined : '1px solid rgba(255,255,255,0.05)',
            borderRadius: mode === 'e2' ? '10px' : undefined,  // E3: z CSS (17.5px)
            padding: mode === 'e2' ? '6px 10px' : '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '0px',
            opacity: (mode === 'e1' && item.done) ? 0.5 : 1,
            transition: `all 300ms ${SPRING}`,
            minHeight: mode === 'e2' ? 'auto' : '38px',
          }}
        >
          {/* E3: tekst + kalendarz obok siebie */}
          {mode === 'e3' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Tekst ustalenia - italic, bez ramki */}
              <div style={{ flex: 1 }}>
                <textarea
                  value={item.text}
                  onChange={(e) => {
                    onChange?.(item.id, { text: e.target.value });
                    onFocusChange?.(item.id, e.target.selectionStart);
                  }}
                  placeholder="Ustalenia..."
                  style={{
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    resize: 'none',
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: '14px',
                    fontWeight: 300,
                    fontFamily: '"Inter", sans-serif',
                    lineHeight: '1.5',
                    padding: 0,
                    margin: 0,
                    overflow: 'hidden',
                    fontStyle: 'italic',
                    height: '42px',
                  }}
                  onClick={(e) => {
                    e.currentTarget.focus();
                    onFocusChange?.(item.id, e.currentTarget.selectionStart);
                  }}
                  onFocus={(e) => {
                    onFocusChange?.(item.id, e.target.selectionStart);
                  }}
                  onSelect={(e: React.SyntheticEvent<HTMLTextAreaElement>) => {
                    onFocusChange?.(item.id, e.currentTarget.selectionStart);
                  }}
                  spellCheck={false}
                />
              </div>

              {/* Ikona kalendarza z plusem po prawej */}
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'date';
                  input.value = item.date ?? '';
                  // Widoczny ale niewidoczny wizualnie — wymagane na iOS/Android żeby change event działał
                  input.style.position = 'fixed';
                  input.style.bottom = '0';
                  input.style.left = '0';
                  input.style.width = '1px';
                  input.style.height = '1px';
                  input.style.opacity = '0';
                  input.style.zIndex = '-1';
                  document.body.appendChild(input);
                  let handled = false;
                  const handleDate = (e: Event) => {
                    console.log('[ArrangementList] handleDate fired - handled:', handled);
                    if (handled) return;
                    handled = true;
                    const newDate = (e.target as HTMLInputElement).value;
                    console.log('[ArrangementList] newDate:', newDate, 'item.id:', item.id);
                    if (newDate) {
                      onChange?.(item.id, { date: newDate });
                      console.log('[ArrangementList] called onChange with date:', newDate);
                    }
                    if (document.body.contains(input)) document.body.removeChild(input);
                  };
                  input.addEventListener('change', handleDate);
                  input.addEventListener('input', handleDate);
                  input.addEventListener('blur', () => {
                    console.log('[ArrangementList] blur fired - handled:', handled);
                    setTimeout(() => {
                      if (!handled) {
                        handled = true;
                        const newDate = input.value;
                        console.log('[ArrangementList] blur - newDate:', newDate, 'item.id:', item.id);
                        if (newDate) {
                          onChange?.(item.id, { date: newDate });
                          console.log('[ArrangementList] blur - called onChange with date:', newDate);
                        }
                      }
                      if (document.body.contains(input)) document.body.removeChild(input);
                    }, 200);
                  });
                  if (input.showPicker) { input.showPicker(); } else { input.click(); }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  flexShrink: 0,
                  opacity: item.date ? 1 : 0.5,
                }}
                title={item.date ? `Termin: ${item.date}` : 'Dodaj datę'}
              >
                <svg width="28" height="28" viewBox="0 0 30 34" fill="none">
                  <path d="M20 8.667V7H10V8.667C10 9.587 9.254 10.333 8.333 10.333C7.413 10.333 6.667 9.587 6.667 8.667V7H5C4.08 7 3.333 7.746 3.333 8.667V12H26.667V8.667C26.667 7.746 25.92 7 25 7H23.333V8.667C23.333 9.587 22.587 10.333 21.667 10.333C20.746 10.333 20 9.587 20 8.667ZM30 14.917C30 15.837 29.254 16.583 28.333 16.583C27.557 16.583 26.904 16.052 26.719 15.333H3.333V28.667C3.333 29.587 4.08 30.333 5 30.333H15C15.92 30.333 16.667 31.08 16.667 32C16.667 32.92 15.92 33.667 15 33.667H5C2.239 33.667 0 31.428 0 28.667V8.667C0 5.905 2.239 3.667 5 3.667H6.667V2C6.667 1.08 7.413 0.333 8.333 0.333C9.254 0.333 10 1.08 10 2V3.667H20V2C20 1.08 20.746 0.333 21.667 0.333C22.587 0.333 23.333 1.08 23.333 2V3.667H25C27.761 3.667 30 5.905 30 8.667V14.917Z" fill="rgba(255,255,255,0.6)"/>
                  <path d="M22.5 18.5V16H19.5V18.5C19.5 19.328 18.828 20 18 20C17.172 20 16.5 19.328 16.5 18.5V16H13.5V20.5C13.5 21.328 14.172 22 15 22H22.5C23.328 22 24 21.328 24 20.5V18.5H22.5ZM25.5 23.75C25.5 24.578 24.828 25.25 24 25.25C23.25 25.25 22.62 24.84 22.4 24.17H10.5V30.5C10.5 31.328 11.172 32 12 32H16.5C17.328 32 18 32.672 18 33.5C18 34.328 17.328 35 16.5 35H12C9.515 35 7.5 32.985 7.5 30.5V18.5C7.5 16.015 9.515 14 12 14H13.5V13.25C13.5 12.422 14.172 11.75 15 11.75C15.828 11.75 16.5 12.422 16.5 13.25V14H19.5V13.25C19.5 12.422 20.172 11.75 21 11.75C21.828 11.75 22.5 12.422 22.5 13.25V14H24C26.485 14 28.5 16.015 28.5 18.5V23.75H25.5Z" fill="rgba(255,255,255,0.8)"/>
                </svg>
              </button>
            </div>
            </div>
          ) : (
            /* E2/E1: wspólny wrapper */
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <span style={{ color: 'rgba(0,255,85,0.45)', fontSize: mode === 'e2' ? '10px' : '11px', fontWeight: 600, minWidth: '16px', paddingTop: mode === 'e2' ? '1px' : '2px' }}>
                  {idx + 1}.
                </span>
                {mode === 'e2' ? (
                  <span style={{ flex: 1, color: 'rgba(255,255,255,0.85)', fontSize: '13px', fontWeight: 300, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.text}>
                    {item.text}
                  </span>
                ) : (
                  <span style={{ flex: 1, color: item.done ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.85)', fontSize: '14px', fontWeight: 300, lineHeight: 1.55, textDecoration: item.done ? 'line-through' : 'none' }}>
                    {item.text}
                  </span>
                )}
                {mode === 'e2' && (
                  <button onClick={() => onRemove?.(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,80,80,0.6)', fontSize: '14px', padding: '2px 4px' }} title="Usuń">🗑</button>
                )}
              </div>
              {mode === 'e1' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '24px' }}>
                  {item.date ? <span style={{ color: 'rgba(0,255,85,0.55)', fontSize: '12px' }}>Termin: {item.date}</span> : <span />}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={item.done} onChange={() => onToggleDone?.(item.id)} style={{ accentColor: '#00FF55', width: '16px', height: '16px' }} />
                    <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px' }}>{item.done ? 'Zrealizowane' : 'Zrealizuj'}</span>
                  </label>
                </div>
              )}
            </>
          )}
        </div>
      ))}
      <style>{arrangementStylesCSS}</style>
    </div>
  )
}
